import { useEffect } from "react";

/**
 * Lightweight SEO head manager. Sets document title, meta description,
 * canonical URL, OG title/description and robots tag from props.
 */
export default function SeoHead({
  title,
  description,
  canonical,
  robots = "index, follow",
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const updateMeta = (name, attr, content) => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return { el, created };
    };

    const upsertLink = (rel, href) => {
      if (!href) return null;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return { el, created };
    };

    const desc = updateMeta("description", "name", description);
    const robotsTag = updateMeta("robots", "name", robots);
    const ogTitle = updateMeta("og:title", "property", title);
    const ogDesc = updateMeta("og:description", "property", description);
    const ogUrl = updateMeta("og:url", "property", canonical);
    const tw = updateMeta("twitter:title", "name", title);
    const twDesc = updateMeta("twitter:description", "name", description);
    const canon = upsertLink("canonical", canonical);

    return () => {
      document.title = prevTitle;
      // No DOM cleanup needed — values get overridden by next page mount.
      // Created tags persist (harmless) to avoid flicker between routes.
      void desc; void robotsTag; void ogTitle; void ogDesc; void ogUrl;
      void tw; void twDesc; void canon;
    };
  }, [title, description, canonical, robots]);

  return null;
}
